import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Dialog, IconButton, makeStyles } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Close } from '@carbon/icons-react';

interface Props {
  isSkip: boolean;
  isPaymentProcessing: React.MutableRefObject<boolean>;
  children: React.ReactNode;
}

const CreatePurchaseOrderBlockProvider = ({
  children,
  isSkip,
  isPaymentProcessing
}: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [isBlock, setIsBlock] = useState(false);

  useEffect(() => {
    if (isSkip) return;

    if (isPaymentProcessing.current) return;

    if (!nextUrl) {
      const block = history.block((location, action) => {
        setNextUrl(
          location.search
            ? location.pathname + location.search
            : location.pathname
        );

        if (action === 'REPLACE' || action === 'PUSH') {
          setIsBlock(true);
          return false;
        }
      });
      return () => block();
    }
  }, [isPaymentProcessing, isSkip, nextUrl, history]);

  const onExit = () => {
    if (nextUrl) {
      history.push(nextUrl);
    } else {
      history.push(history.location.pathname);
    }

    setIsBlock(false);
  };

  const onClose = () => {
    setIsBlock(false);
    setNextUrl(null);
  };

  if (isSkip) return <>{children}</>;

  return (
    <>
      {isBlock && (
        <Dialog
          fullWidth
          open={isBlock}
          onClose={onClose}
          classes={{ paper: classes.paper }}
        >
          <div className={classes.header}>
            <CustomizedTypography
              variant="h4"
              color="textPrimary"
              fontWeight="bold"
            >
              발주서 발행중
            </CustomizedTypography>
            <IconButton className="close" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
          <div className={classes.content}>
            <CustomizedTypography variant="h4" color="textPrimary">
              발주서 발행 중 페이지 이동 시 작성한 내용이 삭제됩니다. 페이지를
              이동하시겠습니까?
            </CustomizedTypography>
          </div>
          <div className={classes.footer}>
            <CustomizedButton
              fullWidth
              variant="contained"
              color="default"
              onClick={onClose}
              size="large"
            >
              돌아가기
            </CustomizedButton>

            <CustomizedButton
              onClick={onExit}
              fullWidth
              variant="contained"
              color="primary"
              size="large"
            >
              나가기
            </CustomizedButton>
          </div>
        </Dialog>
      )}
      {children}
    </>
  );
};

export default CreatePurchaseOrderBlockProvider;

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 476,
    padding: 0,
    borderRadius: 16
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '28px 24px',
    '& .close': {
      padding: 0,
      '& svg': {
        width: 24,
        height: 24
      }
    }
  },
  content: {
    padding: '0px 24px 24px'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    '& button': {
      width: 'calc(100% / 2 - 4px)'
    }
  }
}));
